'use client';

import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { GM_BAR_MOBILE_EXPANDED_EVENT } from '@/components/features/gamification/bars/hooks/useGmBarMobileControls';
import { Link, usePathname } from '@/i18n/routing';
import { cn } from '@/utils/cn';
import { useOpenIntercom } from '../../../../../../hooks/openIntercom';
import { navItems } from './consts/navItems';
import { useIntercomUnread } from './hooks/useIntercomUnread';
import { useMobileNavAnimations } from './hooks/useMobileNavAnimations';
import { getClickHandler } from './utils/getClickHandler';
import { getIsActiveTab } from './utils/getIsActiveTab';
import './styles/mobileNavBar.css';

interface Props {
  handleOpenSideBar: () => void;
  isOpenSideBar: boolean;
  handleCloseSideBar: () => void;
}

function MobileNavBar({ handleOpenSideBar, isOpenSideBar, handleCloseSideBar }: Props) {
  const pathname = usePathname();
  const previousPathRef = useRef(pathname);
  const [isGmBarMobileExpanded, setIsGmBarMobileExpanded] = useState(false);
  const hasUnreadIntercomMessages = useIntercomUnread();
  const { isIntercomOpen, handleSupportClick } = useOpenIntercom();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef<Record<string, HTMLElement | null>>({});

  const setItemRef = (id: string) => (node: HTMLElement | null) => {
    itemRef.current[id] = node;
  };

  useEffect(() => {
    if (previousPathRef.current !== pathname && isOpenSideBar) {
      handleCloseSideBar();
    }
    previousPathRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    const handleGmBarMobileExpanded = (event: Event) => {
      setIsGmBarMobileExpanded((event as CustomEvent<boolean>).detail === true);
    };

    window.addEventListener(GM_BAR_MOBILE_EXPANDED_EVENT, handleGmBarMobileExpanded);
    return () => window.removeEventListener(GM_BAR_MOBILE_EXPANDED_EVENT, handleGmBarMobileExpanded);
  }, []);

  const activeId = navItems.find(({ id, match }) => getIsActiveTab(id, match, pathname, isOpenSideBar, isIntercomOpen))?.id ?? null;
  const { indicator, indicatorBump, shakeId } = useMobileNavAnimations(activeId, containerRef, itemRef);

  return (
    <>
      <div
        ref={containerRef}
        className={cn(
          'fixed rounded-[6.25rem] shadow-[0_-4px_3px_#04000D33] h-16 bottom-[1dvh] left-2.5 right-2.5 z-45 glass-mobile-nav-bar flex justify-between items-center px-1.25 py-1 lg:hidden transition-[opacity,transform] duration-200 ease-out',
          isGmBarMobileExpanded && 'pointer-events-none translate-y-4 opacity-0',
        )}
      >
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute left-0 top-1 bottom-1 rounded-[6.25rem] transition-[transform,width,opacity] duration-500 ease-out will-change-transform',
            indicator.visible ? 'opacity-100' : 'opacity-0',
          )}
          style={{
            width: indicator.width,
            transform: `translate3d(${indicator.left}px, 0, 0)`,
          }}
        >
          <div
            className={cn(
              'relative h-full w-full rounded-[6.25rem] glass-container overflow-hidden',
              indicatorBump ? 'mobile-nav-indicator-bump' : null,
            )}
          >
            <div className="absolute inset-0 rounded-[6.25rem] bg-[radial-gradient(63.25%_93.75%_at_46.99%_100%,rgba(189,77,249,0.25)_0%,rgba(35,19,51,0.25)_100%)]" />
          </div>
        </div>

        {navItems.map(({ id, href, icon, label }) => {
          const handleClick = getClickHandler(id, href, pathname, handleOpenSideBar, handleCloseSideBar, handleSupportClick);

          const hasUnreadSupportMessages = id === 'online-support' && hasUnreadIntercomMessages;

          const itemClassName = 'relative z-10 font-medium text-white text-[0.625rem] leading-[1rem] flex flex-col items-center gap-1 w-[25%] rounded-[6.25rem] py-1.5 transition-colors duration-200';

          return href
            ? (
                <Link
                  key={id}
                  href={href}
                  onClick={handleClick}
                  data-mobile-nav-id={id}
                  ref={setItemRef(id)}
                  className={itemClassName}
                >
                  <Icon
                    name={icon}
                    className={cn(
                      'size-6 transition-colors duration-200',
                      shakeId === id ? 'mobile-nav-content-shake' : null,
                    )}
                  />
                  <span>{label}</span>
                </Link>
              )
            : (
                <button
                  type="button"
                  key={id}
                  data-mobile-nav-id={id}
                  ref={setItemRef(id)}
                  onClick={handleClick}
                  className={itemClassName}
                >
                  <div className="relative">
                    <Icon
                      name={icon}
                      className={cn(
                        'size-6 transition-colors duration-200',
                        shakeId === id ? 'mobile-nav-content-shake' : null,
                      )}
                    />

                    {hasUnreadSupportMessages && (
                      <span className="absolute top-0 -right-2.25 min-w-3.5 h-3.5 text-[0.625rem] bg-primary-2 text-white rounded-full flex items-center justify-center px-1 border-2 border-violet-main-900">
                      </span>
                    )}
                  </div>
                  <span>{label}</span>
                </button>
              );
        })}
      </div>

    </>
  );
};

export default MobileNavBar;
